/**
 * @file Select.tsx
 * @module OverlaySelect
 * @description Kartes klikšķa atlases komponente Karšu pārlūka pārklājumam.
 *              Pārvalda OpenLayers Overlay pozicionēšanu un kartes klikšķa notikumu
 *              apstrādi — nosaka atlasītos objektus, filtrē klasterus, apstrādā
 *              pāradresēšanu uz SIPR sistēmas priekšpuses servisa formām (ierīces,
 *              UVIS brīdinājumi) un atver detalizēto pārklājumu.
 */
import { useObservable } from "@ngneat/use-observable";
import { Map, Overlay } from "ol";
import RenderFeature from "ol/render/Feature";
import { useContext, useEffect } from "react";
import { getMapStore } from "../Map.repository";
import { OverlayManagerContext } from "./Overlay-wrapper";
import Feature, { FeatureLike } from "ol/Feature";
import { buffer, createEmpty, extend, intersects } from "ol/extent";
import { LineString, Point } from "ol/geom";
import { GlobalSettingsContext } from "../../Settings";
import { isMobile } from "react-device-detect";
import { getStyle } from "./Overlay";
import { HOVERLAY_MANAGER } from "../hover/Hover";
import LayerGroup from "ol/layer/Group";
import { determineHoveredStyle } from "../Feature-highlighter";

export const OVERLAY_MANAGER = new Overlay({
	offset: [0, 4],
	autoPan: { animation: { duration: 0 } },
	className: "ol-overlay-container ol-selectable z-10",
});

const getRedirectURL = (feature: FeatureLike) => {
	const internal = feature.get("internal");
	switch (feature.get("layer")?.replace("public.", "") ?? internal?.["originalTable"] ?? internal?.["table"]) {
		case "kafkamessages_uvismessage":
			return window.location.origin + "/driving_conditions/" + feature.get("sipr_id");
		case "kafkamessages_cms":
		case "kafkamessages_sup":
		case "kafkamessages_gup":
		case "kafkamessages_vms":
		case "kafkamessages_camera":
			return window.location.origin + "/devices/edit/" + feature.get("kafka_id");
		default:
			return undefined;
	}
};

const checkClusterFeatures = (feat: Feature | RenderFeature) => {
	const bufferedArea = buffer(feat instanceof RenderFeature ? feat.getExtent() : feat.getGeometry()!.getExtent(), 2.4 * 96);
	return feat.get("features").find((f: Feature) => !intersects(f.getGeometry()!.getExtent()!, bufferedArea));
};

const zoomToCluster = (feat: Feature | RenderFeature, map: Map) => {
	const clusterMembers = feat.get("features");
	if (clusterMembers.length > 1) {
		// Calculate the extent of the cluster members.
		const extent = createEmpty();
		clusterMembers.forEach((feature: Feature) => extend(extent, feature.getGeometry()!.getExtent()));
		const view = map.getView();

		view.fit(extent, { duration: 250, padding: isMobile ? [-150, -150, -150, -150] : [10, 10, 10, 10], maxZoom: 16 });
		const beforeZoom = view.getZoom();
		if (isMobile) {
			setTimeout(() => {
				if (beforeZoom === view.getZoom()) {
					view.animate({ zoom: view.getZoom()! + 1, center: (feat.getGeometry() as Point).getCoordinates(), duration: 300 });
				}
			}, 250);
		}
	}
};

export default function SelectComponent() {
	const { setContext, context } = useContext(OverlayManagerContext);
	const { system_code } = useContext(GlobalSettingsContext);
	const [props] = useObservable(getMapStore());

	const addClick = (map: Map) => {
		map.on("click", (e) => {
			const lg = map
				.getLayers()
				.getArray()
				.find((lg) => lg instanceof LayerGroup && lg.get("type") === "base");
			const overview = lg?.getLayersArray().find((l) => l.getZIndex() === 100);
			if (overview) return;

			let features: Feature[] = [];
			map
				.getFeaturesAtPixel(e.pixel, {
					layerFilter: (l) => l.get("type") !== "base" && l.get("id") !== "search" && l.get("id") !== "draw" && l.get("id") !== undefined,
					hitTolerance: isMobile ? 16 : 2,
				})
				.forEach((feature) => {
					const featureStyle = getStyle(feature, feature.get("internal"));
					if (
						feature.get("layer") !== "public.caps_kilometermarker" &&
						!features.find((f) => {
							const fStyle = getStyle(f, f.get("internal"));
							const fid = f.get("kafka_id") ?? f.get("id");
							const featid = feature.get("kafka_id") ?? feature.get("id");
							return fid === featid && featid !== undefined && featureStyle.layer_id === fStyle.layer_id;
						})
					) {
						features.push(feature as Feature);
					}
				});

			const [feat] = features;

			if (feat && feat.get("features")?.length > 0 && map.getView().getZoom()! < 16 && checkClusterFeatures(feat)) {
				zoomToCluster(feat, map);
			} else if (
				feat &&
				getRedirectURL(feat) &&
				(feat.get("internal")["table"] !== "kafkamessages_uvis" || feat.get("internal")["table"] !== "kafkamessages_uvismessage") &&
				system_code === "SIPR"
			) {
				window.open(getRedirectURL(feat), "_blank");
			} else if (features.length > 0) {
				features = features
					.flatMap((f) => f.get("features") ?? f)
					?.filter((f) => (f instanceof Feature ? f.get("internal")?.["style"] ?? getStyle(f, f.get("internal")) : f));

				features.forEach((f) => {
					const layer = f.get("layer");
					if (layer?.includes("kafkamessages_cms")) {
						const internal = f.get("internal");
						const styles = getStyle(f, internal, true);

						// Split matched styles by sub-layer: Meteostacija (16/17) vs Kameras (110/111)
						const meteoStyle = styles.find((s: any) => s.layerName === "Meteostacija") ?? styles[0];
						const cameraStyle =
							styles.find((s: any) => s.layerName === "Kameras") ??
							styles.find((s: any) => s.layer_id !== meteoStyle?.layer_id);

						if (meteoStyle && cameraStyle && meteoStyle !== cameraStyle) {
							const clone = f.clone();
							// clone() has no id and shares "internal" by reference — detach both
							clone.setId(`${f.getId() ?? f.get("kafka_id")}-camera`);
							f.set("internal", { ...internal, style: meteoStyle }, true);
							clone.set("internal", { ...internal, style: cameraStyle }, true);

							const meteoOlStyle = determineHoveredStyle(map, e, f, { overrideDetermined: false });
							const cameraOlStyle = determineHoveredStyle(map, e, clone, { overrideDetermined: true });

							// Force the defs explicitly — one row renders as meteo, the other as camera,
							// regardless of what determineHoveredStyle resolves internally
							f.setProperties({ overrideStyle: meteoOlStyle, overrideStyleDef: meteoStyle });
							clone.setProperties({ overrideDetermined: true, overrideStyle: cameraOlStyle, overrideStyleDef: cameraStyle }, true);

							features.push(clone);
						}
					}
				});

				setContext((value) => ({ ...value, features: features as any, event: e }));

				const geomType = feat.getGeometry()?.getType();

				setTimeout(() => {
					OVERLAY_MANAGER.setPosition(
						geomType === "Point"
							? (feat.getGeometry() as Point).getFlatCoordinates()
							: geomType === "LineString"
								? (feat.getGeometry() as LineString).getCoordinateAt(0.5)
								: e.coordinate
					);
				}, 50);

				HOVERLAY_MANAGER.setPosition(undefined);
			} else {
				OVERLAY_MANAGER.setPosition(undefined);
				setContext((value) => ({ ...value, features: [] }));
				document.getSelection()?.empty();
			}
		});
	};

	useEffect(() => {
		const map = context.map;
		if (map) {
			map.addOverlay(OVERLAY_MANAGER);
			addClick(map);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.map]);

	return <></>;
}